import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Typography,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { uiConfig } from '../config';
import { DifficultyLevel, PushbackLevel, DIFFICULTY_LABELS, PUSHBACK_LABELS } from '../constants/trainingConstants';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { navigateToTrainingSession } from '../utils/trainingNavigation';

interface TrainingTrack {
  _id: string;
  trackName: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  pushbackLevel: 'low' | 'medium' | 'high';
  status: 'draft' | 'active' | 'archived';
  estimatedDuration: number;
  tags: string[];
  assignedUsers: string[];
  items: any[];
  statistics: {
    totalSessions: number;
    averageScore: number;
    completionRate: number;
  };
  createdAt: string;
  updatedAt: string;
}

const TrainingTracks: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const [tracks, setTracks] = useState<TrainingTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(uiConfig.defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all');
  const [pushbackFilter, setPushbackFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<TrainingTrack | null>(null);
  const [deletingTrack, setDeletingTrack] = useState(false);

  // Fetch tracks
  const fetchTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', String(rowsPerPage));

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (difficultyFilter !== 'all') {
        params.append('difficulty', difficultyFilter);
      }

      if (pushbackFilter !== 'all') {
        params.append('pushbackLevel', pushbackFilter);
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await apiService.get(`/training/tracks?${params.toString()}`);

      if (response.success) {
        setTracks(response.data.tracks || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error fetching training tracks:', error);
      setError(error.error || error.message || 'Failed to fetch training tracks');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, difficultyFilter, pushbackFilter, statusFilter]);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  // Handle create new track
  const handleCreateTrack = () => {
    navigate('/training/builder');
  };

  // Handle edit track
  const handleEditTrack = (track: TrainingTrack) => {
    navigate(`/training/builder/${track._id}`);
  };

  // Handle delete track
  const handleDeleteTrack = async () => {
    if (!selectedTrack) return;

    try {
      setDeletingTrack(true);
      setError(null);

      const response = await apiService.delete(`/training/tracks/${selectedTrack._id}`);

      if (response.success) {
        setSuccess('Training track deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedTrack(null);
        fetchTracks();
      }
    } catch (error: any) {
      console.error('Error deleting training track:', error);
      setError(error.error || error.message || 'Failed to delete training track');
      setDeleteDialogOpen(false);
      setSelectedTrack(null);
    } finally {
      setDeletingTrack(false);
    }
  };

  // Handle publish/unpublish track
  const handleTogglePublish = async (track: TrainingTrack) => {
    try {
      setError(null);

      const endpoint = track.status === 'active'
        ? `/training/tracks/${track._id}/unpublish`
        : `/training/tracks/${track._id}/publish`;

      const response = await apiService.post(endpoint);

      if (response.success) {
        setSuccess(track.status === 'active' ? 'Track unpublished' : 'Track published');
        fetchTracks();
      }
    } catch (error: any) {
      console.error('Error toggling track publish status:', error);
      setError(error.error || error.message || 'Failed to update track status');
    }
  };

  const handleAssignUsers = (track: TrainingTrack) => {
    navigate(`/training/tracks/${track._id}/assign`);
  };

  // Handle start training (navigate to frontend via SSO)
  const handleStartTraining = async (track: TrainingTrack) => {
    try {
      setError(null);
      await navigateToTrainingSession(track._id, track.trackName);
      setSuccess('Opening training session in new tab...');
    } catch (error: any) {
      console.error('Error starting training session:', error);
      setError(error.error || error.message || 'Failed to start training session');
    }
  };

  // Open delete dialog
  const openDeleteDialog = (track: TrainingTrack) => {
    setSelectedTrack(track);
    setDeleteDialogOpen(true);
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get pushback color
  const getPushbackColor = (level: string) => {
    switch (level) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'default';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
      <Box>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
          <Box>
            <IconButton onClick={fetchTracks} color="primary">
              <RefreshIcon />
            </IconButton>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTrack}
                data-tutorial="create-track-button"
              >
                Create Track
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          {/* Filters */}
          <Box display="flex" gap={2} mb={3} data-tutorial="training-filters">
            <TextField
              placeholder="Search by track name or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                label="Difficulty"
              >
                <MenuItem value="all">All</MenuItem>
                {Object.values(DifficultyLevel).map((level) => (
                  <MenuItem key={level} value={level}>
                    {DIFFICULTY_LABELS[level]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Pushback</InputLabel>
              <Select
                value={pushbackFilter}
                onChange={(e) => setPushbackFilter(e.target.value)}
                label="Pushback"
              >
                <MenuItem value="all">All</MenuItem>
                {Object.values(PushbackLevel).map((level) => (
                  <MenuItem key={level} value={level}>
                    {PUSHBACK_LABELS[level]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Track Name</TableCell>
                  <TableCell>Difficulty</TableCell>
                  <TableCell>Pushback</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Assigned Users</TableCell>
                  <TableCell>Sessions</TableCell>
                  <TableCell>Avg Score</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : tracks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No training tracks found. Create your first track to get started!
                    </TableCell>
                  </TableRow>
                ) : (
                  tracks.map((track) => (
                    <TableRow key={track._id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {track.trackName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {track.description?.substring(0, 50)}
                            {track.description?.length > 50 ? '...' : ''}
                          </Typography>
                          {track.tags?.length > 0 && (
                            <Stack direction="row" spacing={0.5} mt={0.5}>
                              {track.tags.slice(0, 2).map((tag, index) => (
                                <Chip key={index} label={tag} size="small" variant="outlined" />
                              ))}
                              {track.tags.length > 2 && (
                                <Chip label={`+${track.tags.length - 2}`} size="small" variant="outlined" />
                              )}
                            </Stack>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={DIFFICULTY_LABELS[track.difficulty as DifficultyLevel] || track.difficulty}
                          color={getDifficultyColor(track.difficulty)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={PUSHBACK_LABELS[track.pushbackLevel as PushbackLevel]?.split(' ')[0] || track.pushbackLevel}
                          color={getPushbackColor(track.pushbackLevel)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={track.status}
                          color={getStatusColor(track.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{track.items?.length || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={track.assignedUsers?.length || 0}
                          size="small"
                          icon={<PeopleIcon />}
                          clickable
                        />
                      </TableCell>
                      <TableCell>
                        {track.statistics?.totalSessions || 0}
                      </TableCell>
                      <TableCell>
                        {track.statistics?.averageScore
                          ? `${track.statistics.averageScore.toFixed(1)}%`
                          : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        {/* Show management actions only for admins */}
                        {isAdmin && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditTrack(track)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={track.status === 'active' ? 'Unpublish' : 'Publish'}>
                              <IconButton
                                size="small"
                                onClick={() => handleTogglePublish(track)}
                                color={track.status === 'active' ? 'warning' : 'success'}
                              >
                                {track.status === 'active' ? <PauseIcon /> : <PlayIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => openDeleteDialog(track)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {/* Show "Start Training" for all users (including admins) if track is active */}
                        {track.status === 'active' && (
                          <Tooltip title="Start Training">
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => handleStartTraining(track)}
                              sx={{ ml: 1 }}
                            >
                              Start
                            </Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Delete Track Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !deletingTrack && setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Training Track</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the track "{selectedTrack?.trackName}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deletingTrack}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTrack}
              variant="contained"
              color="error"
              disabled={deletingTrack}
              startIcon={deletingTrack ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {deletingTrack ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default TrainingTracks;
